import {
    ChangeEventHandler,
    forwardRef,
    MouseEventHandler,
    PropsWithChildren,
    useCallback,
    useImperativeHandle,
} from "react"
import { classes } from "./react"

interface SharedProps {
    checked: boolean
    cssClass?: string
    /**
     * Whether the checkbox should be rendered with FontAwesome icons or with the (styled) HTML input.
     * @default true
     */
    icons?: boolean
    /**
     * If using `icons`, specify an icon CSS classname to use for "checked" state.
     * @default "fas fa-check"
     */
    iconChecked?: string
    /**
     * If using `icons`, specify an icon CSS classname to use for "unchecked" state.
     * @default "far fa-square"
     */
    iconUnchecked?: string
    /**
     * If using `icons`, specify an icon CSS classname to use for the "indeterminate" state.
     * @default "far fa-square-minus"
     */
    iconIndeterminate?: string
    /**
     * If using `icons`, specify an icon CSS classname to use for the "disabled" state (whether checked/unchecked)
     * @default undefined
     */
    iconDisabled?: string
    disabled?: boolean
    title?: string
    /**
     * Whether this checkbox should appear "indeterminate" when "checked".
     */
    indeterminate?: boolean

    /**
     * The native element class name
     */
    className?: string
    /**
     * The input name
     */
    name?: string
    /**
     * The native element click event.
     */
    onClick?: MouseEventHandler<HTMLInputElement>
    /**
     * The native input change event
     */
    onChange?: ChangeEventHandler<HTMLInputElement>
}

type Props =
    | (SharedProps & {
          setChecked: (checked: boolean) => void
          disabled?: boolean
      })
    | (SharedProps & {
          setChecked?: never
          disabled: true
      })

type CheckboxProps = PropsWithChildren<Props>

export const Checkbox = forwardRef(
    (
        {
            className,
            children,
            checked,
            setChecked,
            onChange,
            onClick,
            indeterminate = false,
            cssClass,
            disabled,
            icons = true,
            iconChecked = "fas fa-check",
            iconIndeterminate = "far fa-square-minus",
            iconUnchecked = "far fa-square",
            iconDisabled = undefined,
            title,
            name,
        }: CheckboxProps,
        ref
    ) => {
        // TODO: animate

        // We'll need a ref to access the input element directly.
        const inputRef = React.useRef<HTMLInputElement>()
        // This handle used to synchronise two refs. In this case, the internal ref (`inputRef`) with any ref passed from outside (`ref`)
        useImperativeHandle(ref, () => inputRef.current)
        // Synchronise the "indeterminate" prop with the HTML checkbox.
        React.useEffect(() => {
            const el = inputRef.current
            if (el) {
                el.indeterminate = indeterminate && checked
            }
        }, [indeterminate, checked])

        // At some point in the past, we wrapped our checkbox's `onChange` event handler as a `setChecked` event handler.
        // This means that the checkbox has a non-standard interface so it can't be swapped-in for a normal checkbox, in some places.
        // This part here is so that we can support both.
        const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
            (event) => {
                onChange?.(event)
                setChecked?.(event.target.checked)
            },
            [onChange, setChecked]
        )

        return (
            <div
                className={classes(
                    "checkbox",
                    icons ? "icons" : "checkbox-styled",
                    { disabled: disabled },
                    cssClass,
                    className
                )}
                title={title}
            >
                <label>
                    <input
                        ref={inputRef}
                        name={name}
                        type="checkbox"
                        checked={!!checked}
                        disabled={disabled || false}
                        onClick={onClick}
                        onChange={handleChange}
                    />
                    {icons && (
                        <i
                            className={
                                iconDisabled && disabled
                                    ? iconDisabled
                                    : checked
                                    ? indeterminate
                                        ? iconIndeterminate
                                        : iconChecked
                                    : iconUnchecked
                            }
                        />
                    )}
                    <span>{children}</span>
                </label>
            </div>
        )
    }
)
